import { t } from "@bookingcom/lingojs-core";
import React, { useMemo } from "react";
import { Text } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import useFormatPrice from "../../../../hooks/useFormatPrice.js";
import getLowestAdultTravellerPrice from "../../../../utils/lowest-adult-traveller-price.js";
import { isOfMetaOrigin } from "../../../../utils/marketing-url-params.js";
import useUserAgent from "../../../../hooks/useUserAgent.js";
import useRouteName from "../../../../hooks/useRouteName.js";
import useGetSearchResultsBound from "../../../../hooks/useGetSearchResultsBound.js";
import { UnifiedPriceTag } from "../../UnifiedPriceTag/index.js";
import { getAttachedAncillaries } from "../../UnifiedPriceBreakdown/index.js";
const FareCardPrice = (props) => {
    const { fareOffer, isBold, showPricePerPerson = false, totalPriceLabel, variant } = props;
    const i18n = useI18n();
    const { formatPrice } = useFormatPrice();
    const routeName = useRouteName();
    const { isMobile } = useUserAgent();
    const adultTravellerPrice = getLowestAdultTravellerPrice(fareOffer.travellerPrices);
    const isRoundPrice = variant === "desktop-search-results" || (!isOfMetaOrigin() && isMobile);
    const multipleTravellers = fareOffer.travellerPrices.length > 1;
    const totalPrice = useMemo(() => {
        return isRoundPrice && fareOffer.priceBreakdown.totalRounded
            ? formatPrice(fareOffer.priceBreakdown.totalRounded, { decimalPlaces: 0 })
            : formatPrice(fareOffer.priceBreakdown.total);
    }, [fareOffer.priceBreakdown.total, fareOffer.priceBreakdown.totalRounded, formatPrice, isRoundPrice]);
    const strikethroughPrice = useMemo(() => {
        const shouldShowStrikethrough = fareOffer.priceBreakdown.showPriceStrikethrough;
        if (!shouldShowStrikethrough) {
            return undefined;
        }
        return isRoundPrice && fareOffer.priceBreakdown.totalWithoutDiscountRounded
            ? fareOffer.priceBreakdown.totalWithoutDiscountRounded
            : fareOffer.priceBreakdown.totalWithoutDiscount;
    }, [
        fareOffer.priceBreakdown.showPriceStrikethrough,
        fareOffer.priceBreakdown.totalWithoutDiscount,
        fareOffer.priceBreakdown.totalWithoutDiscountRounded,
        isRoundPrice
    ]);
    const bound = useGetSearchResultsBound();
    const shouldHidePriceExplanation = useMemo(() => {
        const isSolo = [...fareOffer.travellerPrices].length === 1;
        return (isSolo &&
            [
                "searchresults",
                "flightdetails",
                "searchresults-outbound",
                "searchresults-inbound",
                "flightdetails-outbound",
                "flightdetails-inbound"
            ].includes(routeName));
    }, [fareOffer.travellerPrices, routeName]);
    const priceTagProps = useMemo(() => {
        const ticketPrice = fareOffer.priceBreakdown;
        const productId = fareOffer.unifiedPriceBreakdown?.id;
        const price = isRoundPrice && ticketPrice.totalRounded ? ticketPrice.totalRounded : ticketPrice.total;
        return {
            productId,
            price,
            shouldRoundPrice: isRoundPrice
        };
    }, [fareOffer.priceBreakdown, isRoundPrice, fareOffer.unifiedPriceBreakdown?.id]);
    const priceTagPropsWithPricePerAdult = useMemo(() => {
        if (!adultTravellerPrice)
            return priceTagProps;
        const pricePerAdult = isRoundPrice && adultTravellerPrice.travellerPriceBreakdown.totalRounded
            ? adultTravellerPrice.travellerPriceBreakdown.totalRounded
            : adultTravellerPrice.travellerPriceBreakdown.total;
        if (bound === "inbound") {
            const travellerFarePriceDifference = fareOffer.priceBreakdown.travellerFarePriceDifference;
            if (travellerFarePriceDifference?.units !== undefined) {
                const pricePerAdultTranslations = [
                    (formattedPrice) => formattedPrice.replace("-", ""),
                    (formattedPrice) => travellerFarePriceDifference.units < 0
                        ? i18n.trans(t("flights_sr_split_bounds_us_incremental_minus", { variables: { price: formattedPrice } }))
                        : i18n.trans(t("flights_sr_split_bounds_us_incremental_plus", { variables: { price: formattedPrice } }))
                ];
                return {
                    ...priceTagProps,
                    pricePerAdult: travellerFarePriceDifference,
                    pricePerAdultTranslations,
                    shouldShowPricePerAdult: !!adultTravellerPrice
                };
            }
        }
        return {
            ...priceTagProps,
            pricePerAdult,
            shouldShowPricePerAdult: showPricePerPerson && multipleTravellers && Boolean(adultTravellerPrice)
        };
    }, [
        showPricePerPerson,
        priceTagProps,
        adultTravellerPrice,
        multipleTravellers,
        isRoundPrice,
        bound,
        fareOffer.priceBreakdown.travellerFarePriceDifference,
        i18n
    ]);
    const ariaAttributes = useMemo(() => {
        const attributes = {};
        if (bound === "inbound") {
            const totalPrice = formatPrice(priceTagPropsWithPricePerAdult.price);
            const pricePerPerson = priceTagPropsWithPricePerAdult.pricePerAdult
                ? formatPrice(priceTagPropsWithPricePerAdult.pricePerAdult)
                : "";
            const formattedPriceDiff = priceTagPropsWithPricePerAdult?.pricePerAdultTranslations?.[1](pricePerPerson) ?? "";
            attributes["aria-label"] = `${formattedPriceDiff} ${i18n.trans(t("flights_sr_price_multi_total", { variables: { total_price: totalPrice } }))}`;
        }
        else {
            const ariaPerPersonPrice = adultTravellerPrice
                ? formatPrice(adultTravellerPrice.travellerPriceBreakdown.total)
                : formatPrice(fareOffer.priceBreakdown.total);
            const ariaTotalPriceLabel = `${variant === "desktop-search-results"
                ? i18n.trans(t("flights_sr_price_multi_total", { variables: { total_price: totalPrice } }))
                : i18n.trans(t("flights_sr_price_multi", { variables: { total_price: totalPrice } }))}`;
            let label;
            if (strikethroughPrice) {
                const oldPrice = formatPrice(strikethroughPrice, { decimalPlaces: isRoundPrice ? 0 : undefined });
                label = multipleTravellers
                    ? [
                        i18n.trans(t("a11y_flights_deal_price_new", { variables: { new_price: ariaPerPersonPrice } })),
                        i18n.trans(t("a11y_flights_deal_price_old_solo", {
                            variables: {
                                old_price: oldPrice
                            }
                        })),
                        ariaTotalPriceLabel
                    ].join(", ")
                    : [
                        i18n.trans(t("a11y_flights_deal_price_old_solo", {
                            variables: {
                                old_price: oldPrice
                            }
                        })),
                        i18n.trans(t("a11y_flights_deal_price_new", { variables: { new_price: totalPrice } }))
                    ].join(", ");
            }
            else if (multipleTravellers) {
                label = showPricePerPerson ? `${ariaPerPersonPrice}, ${ariaTotalPriceLabel}` : ariaTotalPriceLabel;
            }
            else {
                label = ariaPerPersonPrice;
            }
            attributes["aria-label"] = label;
        }
        return attributes;
    }, [
        bound,
        formatPrice,
        priceTagPropsWithPricePerAdult.price,
        priceTagPropsWithPricePerAdult.pricePerAdult,
        priceTagPropsWithPricePerAdult?.pricePerAdultTranslations,
        i18n,
        adultTravellerPrice,
        fareOffer.priceBreakdown.total,
        variant,
        totalPrice,
        strikethroughPrice,
        multipleTravellers,
        isRoundPrice,
        showPricePerPerson
    ]);
    const unifiedPriceTagPriceProps = useMemo(() => {
        const pricePropsVariant = isBold ? "strong_2" : "strong_1";
        const pricePropsAttributes = {
            "aria-hidden": true,
            "data-testid": "flight_card_price_main_price"
        };
        const attachedAncillaries = getAttachedAncillaries(fareOffer.unifiedPriceBreakdown);
        return {
            ...priceTagPropsWithPricePerAdult,
            priceAlignment: "start",
            priceProps: {
                attributes: pricePropsAttributes,
                tagName: "div",
                variant: pricePropsVariant
            },
            attachedAncillaries,
            strikethroughPrice,
            strikethroughPricePosition: "start",
            strikethroughPriceProps: { variant: isMobile ? "body_2" : "small_1" }
        };
    }, [priceTagPropsWithPricePerAdult, isBold, fareOffer.unifiedPriceBreakdown, strikethroughPrice, isMobile]);
    const unifiedPriceTagExplanationProps = useMemo(() => {
        if (adultTravellerPrice) {
            const pricePerAdultExplanationProps = {
                variant: "small_1",
                attributes: {
                    "aria-hidden": true,
                    "data-testid": "flight_card_price_total_price"
                }
            };
            const pricePerAdultExplanationTranslations = [];
            if (bound === "inbound") {
                pricePerAdultExplanationTranslations.push((formattedPrice) => i18n.trans(t("flights_sr_split_bounds_us_total", { variables: { price: formattedPrice } })));
            }
            else {
                if (!shouldHidePriceExplanation) {
                    pricePerAdultExplanationTranslations.push((formattedPrice) => multipleTravellers
                        ? variant === "desktop-search-results"
                            ? i18n.trans(t("flights_sr_price_multi_total", { variables: { total_price: formattedPrice } }))
                            : i18n.trans(t("flights_sr_price_multi", { variables: { total_price: formattedPrice } }))
                        : i18n.trans(t("flights_price_qualifier_total_for_all")));
                }
            }
            return {
                priceExplanation: totalPriceLabel,
                pricePerAdultExplanationProps,
                pricePerAdultExplanationTranslations
            };
        }
        return;
    }, [adultTravellerPrice, bound, totalPriceLabel, i18n, shouldHidePriceExplanation, multipleTravellers, variant]);
    return (React.createElement("div", null,
        bound === "inbound" && (React.createElement(Text, { variant: "small_1", color: "neutral_alt" }, multipleTravellers
            ? i18n.trans(t("flights_sr_split_bounds_us_round_trip_per"))
            : i18n.trans(t("flights_sr_split_bounds_us_round_trip")))),
        React.createElement(UnifiedPriceTag, { ...unifiedPriceTagPriceProps, ...unifiedPriceTagExplanationProps, attributes: ariaAttributes })));
};
export default FareCardPrice;
